/**
 * The write side of an object param edit.
 *
 * ⚠️ A bare `updateObject({id, width})` RESIZES NOTHING for a vector or a group. A group's and a path-like vector's size is scale over their extent, so width/height write `scaleX`/`scaleY`; a brush rebuilds its outline when a width prop changes. PropertiesPanel and the automation runner both write through here.
 *
 * `applyObjectWrite` returns true when it has already dispatched, false when the caller should do the plain write.
 */
import { holdObject, releaseObjects } from '~/lib/automation'
import {
	getGroupChildrenBbox,
	getObjectBounds,
	localExtent,
	localToParent,
	originOf,
	positionFromOrigin,
	SCALE_SIZED,
} from '~/lib/objectBounds'
import { getObjectInitial } from '~/lib/params'
import { buildPivotUpdates } from '~/lib/pivotObject'
import { buildOutlineFromCenterline, normalizeBrushOutline, refitBrushPath } from '~/lib/svg/brushUtils'
import { rawPathToString, stringToRawPath } from '~/lib/svg/paths'
import { updateObject, updateObjects } from '~/store/live'

// A slider drag's props per object id, drawn on Pixi until `settleObjectWrites` dispatches them.
const pending = new Map()

/** One drag frame: `edit(write)`'s writes drawn on Pixi, not dispatched. `objects` must be the pre-drag ones, so a frame replaces the last instead of compounding. Returns the held props per id. */
export const holdObjectWrites = (objects, edit) => {
	edit(({ payload: { id, ...props } }) => pending.set(id, { ...pending.get(id), ...props }))
	for (const [id, props] of pending) {
		const entry = window.pixi_objects_map?.get(id)
		if (entry && objects[id]) window.pixi_update_object?.(entry.pixiObj, holdObject(id, objects[id], props))
	}
	window.pixi_app?.renderer.render(window.pixi_app.stage)
	return pending
}

/** The drag's release: its held props in one dispatch. `skipHistory`, as the mousedown pushed the gesture's entry. */
export const settleObjectWrites = dispatch => {
	if (!pending.size) return
	const updates = [...pending].map(([id, props]) => ({ id, ...props }))
	pending.clear()
	dispatch({ ...updateObjects(updates), meta: { skipHistory: true } })
	releaseObjects()
}

/** A release that writes its own final value: the held frames are dropped, not dispatched. True when a drag was held. */
export const dropObjectWrites = () => {
	const held = pending.size > 0
	pending.clear()
	releaseObjects()
	return held
}

/* A brush's outline is DERIVED from its centerline and width profile, so changing any of those without refitting leaves the stored outline describing the old stroke. */
export const applyBrushRefit = (dispatch, obj, name, value) => {
	if (obj.type !== 'brush') return false

	if (
		(name === 'smoothness' || name === 'strokeCap' || name === 'widthProfile') &&
		(obj.rawPoints || obj.centerline)
	) {
		const smoothness = name === 'smoothness' ? value : obj.smoothness
		const strokeCap = name === 'strokeCap' ? value : obj.strokeCap
		const widthProfile = name === 'widthProfile' ? value : obj.widthProfile
		const mw = obj.minWidth || 0
		const sw = mw + (obj.widthGrowth ?? (obj.maxWidth ? obj.maxWidth - mw : 8))
		if (obj.rawPoints) {
			const result = refitBrushPath(obj.rawPoints, smoothness, strokeCap, sw, mw, widthProfile)
			if (result) {
				normalizeBrushOutline(result)
				const bounds = getObjectBounds(
					{ type: 'brush', x: result.x, y: result.y, data: result.data, variableWidth: true },
					{}
				)
				// `result.x/y` is the refit's data origin; the stored position is the pivot.
				const pos = positionFromOrigin({ ...obj, data: result.data }, result)
				dispatch(
					updateObject({
						id: obj.id,
						[name]: value,
						data: result.data,
						centerline: result.centerline,
						x: pos.x,
						y: pos.y,
						width: bounds.width,
						height: bounds.height,
					})
				)
			}
		} else {
			// Centerline fallback: rebuild from centerline
			const clRaw = stringToRawPath(obj.centerline)
			const origin = originOf(obj)
			const absSegment = clRaw[0].slice()
			for (let i = 0; i < absSegment.length; i++) {
				absSegment[i] += i % 2 ? origin.y : origin.x
			}
			const absCenterline = rawPathToString([absSegment])
			const outResult = buildOutlineFromCenterline(absCenterline, sw, strokeCap, smoothness, mw, widthProfile)
			if (outResult) {
				normalizeBrushOutline(outResult)
				const clShifted = absSegment.slice()
				for (let i = 0; i < clShifted.length; i++) {
					clShifted[i] -= i % 2 ? outResult.y : outResult.x
				}
				const centerlineLocal = rawPathToString([clShifted])
				const bounds = getObjectBounds(
					{ type: 'brush', x: outResult.x, y: outResult.y, data: outResult.data, variableWidth: true },
					{}
				)
				const pos = positionFromOrigin({ ...obj, data: outResult.data }, outResult)
				dispatch(
					updateObject({
						id: obj.id,
						[name]: value,
						data: outResult.data,
						centerline: centerlineLocal,
						x: pos.x,
						y: pos.y,
						width: bounds.width,
						height: bounds.height,
					})
				)
			}
		}
		return true
	}

	if ((name === 'minWidth' || name === 'widthGrowth') && obj.variableWidth && (obj.rawPoints || obj.centerline)) {
		const mw = name === 'minWidth' ? value : obj.minWidth || 0
		const growth =
			name === 'widthGrowth'
				? value
				: (obj.widthGrowth ?? (obj.maxWidth ? obj.maxWidth - (obj.minWidth || 0) : 8))
		const sw = mw + growth
		if (obj.centerline) {
			const clRaw = stringToRawPath(obj.centerline)
			const origin = originOf(obj)
			const absSegment = clRaw[0].slice()
			for (let i = 0; i < absSegment.length; i++) {
				absSegment[i] += i % 2 ? origin.y : origin.x
			}
			const absCenterline = rawPathToString([absSegment])
			const outResult = buildOutlineFromCenterline(
				absCenterline,
				sw,
				obj.strokeCap,
				obj.smoothness,
				mw,
				obj.widthProfile
			)
			if (outResult) {
				// Normalize outline so getObjectBounds returns x=obj.x
				normalizeBrushOutline(outResult)
				const clShifted = absSegment.slice()
				for (let i = 0; i < clShifted.length; i++) {
					clShifted[i] -= i % 2 ? outResult.y : outResult.x
				}
				const centerlineLocal = rawPathToString([clShifted])
				const bounds = getObjectBounds(
					{ type: 'brush', x: outResult.x, y: outResult.y, data: outResult.data, variableWidth: true },
					{}
				)
				const pos = positionFromOrigin({ ...obj, data: outResult.data }, outResult)
				dispatch(
					updateObject({
						id: obj.id,
						[name]: value,
						data: outResult.data,
						centerline: centerlineLocal,
						x: pos.x,
						y: pos.y,
						width: bounds.width,
						height: bounds.height,
					})
				)
			}
		} else {
			const result = refitBrushPath(obj.rawPoints, obj.smoothness, obj.strokeCap, sw, mw, obj.widthProfile)
			if (result) {
				const bounds = getObjectBounds(
					{ type: 'brush', x: obj.x, y: obj.y, data: result.data, variableWidth: true },
					{}
				)
				// Same data frame, new extent: keep the frame's origin where it was.
				const pos = positionFromOrigin({ ...obj, data: result.data }, originOf(obj))
				const update = {
					id: obj.id,
					[name]: value,
					data: result.data,
					x: pos.x,
					y: pos.y,
					width: bounds.width,
					height: bounds.height,
				}
				if (result.centerline) update.centerline = result.centerline
				dispatch(updateObject(update))
			}
		}
		return true
	}

	return false
}

/**
 * Dispatch one param write for one object, deriving whatever geometry the param implies.
 *
 * @param sizeLinked when true, width and height scale together; false moves each axis alone.
 * @returns true if it dispatched; false if the caller should write the param plainly.
 */
export const applyObjectWrite = (dispatch, obj, objects, name, value, sizeLinked = false) => {
	if (applyBrushRefit(dispatch, obj, name, value)) return true

	/* The pivot moves, the picture stays: `x`/`y` follow it, as the stage's pivot tool does. A group's pivot is its origin, never these fractions. */
	if ((name === 'pivotX' || name === 'pivotY') && obj.type !== 'group') {
		const e = localExtent(obj, objects)
		const fx = name === 'pivotX' ? value : (obj.pivotX ?? 0.5)
		const fy = name === 'pivotY' ? value : (obj.pivotY ?? 0.5)
		const point = localToParent(obj, objects, { x: e.x + fx * e.width, y: e.y + fy * e.height })
		for (const update of buildPivotUpdates(obj, objects, point)) dispatch(updateObject(update))
		return true
	}

	/* Rotation is about the pivot, which `x`/`y` name, so it is a lone field on every type. */
	if (name === 'rotation') {
		dispatch(updateObject({ id: obj.id, rotation: value }))
		return true
	}

	/* A group's or path-like vector's size is SCALE over its extent (children, or `data`); storing width too is a second copy. The scale is about the pivot, so the position stays; its sign (the mirror) is kept. */
	if ((name === 'width' || name === 'height') && SCALE_SIZED.has(obj.type) && (obj.type === 'group' || obj.data)) {
		const e = obj.type === 'group' ? getGroupChildrenBbox(obj, objects) : localExtent(obj, objects)
		const sx = obj.scaleX ?? 1
		const sy = obj.scaleY ?? 1
		const signed = (s, magnitude) => (s < 0 ? -magnitude : magnitude)
		const base = name === 'width' ? e.width : e.height
		if (!(base > 0)) return true
		// Linked: both axes keep their ratio, so each scales by the factor this axis moved.
		if (sizeLinked) {
			const k = value / (base * Math.abs(name === 'width' ? sx : sy) || 1)
			dispatch(updateObject({ id: obj.id, scaleX: sx * k, scaleY: sy * k }))
		} else if (name === 'width') dispatch(updateObject({ id: obj.id, scaleX: signed(sx, value / base) }))
		else dispatch(updateObject({ id: obj.id, scaleY: signed(sy, value / base) }))
		return true
	}

	if (sizeLinked && (name === 'width' || name === 'height')) {
		// ⚠️ RESOLVED, never read raw: `live.objects` is sparse, so a dimension at its initial is ABSENT and `obj.height || 0` collapses the other axis to zero.
		const w = obj.width ?? getObjectInitial(obj.type, 'width') ?? 0
		const h = obj.height ?? getObjectInitial(obj.type, 'height') ?? 0
		const ratio = name === 'width' ? value / (w || 1) : value / (h || 1)
		dispatch(updateObject({ id: obj.id, width: w * ratio, height: h * ratio }))
		return true
	}

	return false
}
